import React, { useState } from "react";
import { NXAlertCircle } from "../../icons";
import { RootState } from "../../redux/rootReducer";
import { useSelector } from "react-redux";

interface CurrencyInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  label?: string
  errorData?: Record<string, string[]>;
  labelProps?: Record<string, any>;
  helpText?: string;
  infoClassName?: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  defaultValue?: any;
  currencyCode?: string; // Currency code like USD, EUR, etc.
  currencyPosition?: 'left' | 'right'; // Position of currency symbol/code
}

const CurrencyInputField: React.FC<CurrencyInputProps> = ({
  className,
  errorData,
  name,
  label,
  labelProps,
  helpText,
  infoClassName,
  placeholder,
  onChange,
  defaultValue,
  currencyCode,
  currencyPosition = 'left',
  ...props
}) => {
  const errorMessages = name && errorData && errorData[name] ? errorData[name] : [];
  const [inputValue, setInputValue] = useState(defaultValue || "");
  const me = useSelector((state: RootState) => state.auth.me);


  const getCurrencyCode = currencyCode ? currencyCode : me?.baseCurrency || "USD";

  // Get currency symbol based on currency code
  const getCurrencySymbol = (code: string): string => {
    const currencyMap: Record<string, string> = {
        USD: "$",
        EUR: "€",
        GBP: "£",
        JPY: "¥",
        INR: "₹",
        AUD: "A$",
        CAD: "C$",
        CHF: "CHF",
        CNY: "¥",
        DKK: "kr",
        HKD: "HK$",
        KRW: "₩",
        MXN: "$",
        MYR: "RM",
        NOK: "kr",
        NZD: "NZ$",
        PHP: "₱",
        SEK: "kr",
        SGD: "S$",
        THB: "฿",
        ZAR: "R",
        BRL: "R$",
        CZK: "Kč",
        HUF: "Ft",
        IDR: "Rp",
        ILS: "₪",
        PLN: "zł",
        RUB: "₽",
        TRY: "₺",
        AED: "د.إ",
        ARS: "$",
        BGN: "лв",
        CLP: "$",
        COP: "$",
        EGP: "£",
        HRK: "kn",
        ISK: "kr",
        KES: "KSh",
        KWD: "د.ك",
        LKR: "Rs",
        MAD: "د.م.",
        NGN: "₦",
        PKR: "₨",
        QAR: "ر.ق",
        RON: "lei",
        SAR: "﷼",
        TWD: "NT$",
        UAH: "₴",
        VND: "₫",
        BHD: ".د.ب",
        OMR: "ر.ع.",
        JOD: "د.ا",
        KZT: "₸",
        LBP: "ل.ل",
        BDT: "৳",
        GEL: "₾",
        BOB: "Bs.",
        CRC: "₡",
        DOP: "RD$",
        FJD: "FJ$",
        GHS: "GH₵",
        GIP: "£",
        GTQ: "Q",
        HNL: "L",
        JMD: "J$",
        KHR: "៛",
        LAK: "₭",
        LSL: "L",
        MGA: "Ar",
        MOP: "MOP$",
        MUR: "₨",
        NAD: "N$",
        NIO: "C$",
        PAB: "B/.",
        PEN: "S/",
        PGK: "K",
        PYG: "₲",
        RWF: "FRw",
        SBD: "SI$",
        SCR: "₨",
        SLL: "Le",
        SZL: "E",
        TJS: "SM",
        TMT: "T",
        TOP: "T$",
        UYU: "$U",
        VUV: "VT",
        WST: "WS$",
        XAF: "FCFA",
        XCD: "EC$",
        XOF: "CFA",
        XPF: "₣",
    };
    
    return currencyMap[code] || code;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (onChange) {
      onChange(e);
    }
  };

  const currencySymbol = getCurrencySymbol(getCurrencyCode);

  return (
    <div>
      {label && (
        <div className="flex items-center space-x-2 mb-1">
          <label htmlFor={name} {...labelProps} className="text-sm font-medium text-gray-700">
            {label}
          </label>
          <div className="relative group inline-block">
            <NXAlertCircle className={infoClassName ? infoClassName : "text-base-300"} />
            {helpText && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity z-50">
                {helpText}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="relative">
        {currencyPosition === 'left' && (
          <div className={`absolute inset-y-0 left-0 flex items-center ${currencySymbol.length >= 3 ? 'pl-2' : 'pl-3'} pointer-events-none`}>
            <span className="text-gray-500 sm:text-sm">{currencySymbol}</span>
          </div>
        )}
        <input
          {...props}
          onChange={handleChange}
          name={name}
           placeholder={placeholder ? placeholder : `0.00 ${getCurrencyCode}`}
          defaultValue={inputValue || defaultValue}
          className={`block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
            currencyPosition === 'left' ? 'pl-8' : 'pr-8'
          } ${className ? className : ""} ${errorMessages.length > 0 ? "border-red-500" : ""}`}
        />
        {currencyPosition === 'right' && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <span className="text-gray-500 sm:text-sm">{currencySymbol}</span>
          </div>
        )}
      </div>
      {errorMessages.length > 0 && (
        <div className="text-red-600 text-sm mt-1">
          {errorMessages.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CurrencyInputField;